import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Menu, Bell, Search, ChevronRight, LogOut, User, Settings } from 'lucide-react';
import { useSidebar } from '../../context/SidebarContext';
import { useAuth } from '../../context/AuthContext';
import { ThemeToggle } from '../ui/Toggle/ThemeToggle';

function getBreadcrumb(path: string): string[] {
  const map: Record<string, string> = {
    dashboard: 'Dashboard',
    analytics: 'Analytics',
    content: 'Content',
    home: 'Home Page',
    certifications: 'Certifications',
    contact: 'Contact',
    about: 'About',
    files: 'File Manager',
    pages: 'Pages',
    settings: 'Settings',
  };
  return path.split('/').filter(Boolean).map(s => map[s] || s);
}

export default function Topbar() {
  const { openMobile } = useSidebar();
  const { user, logout } = useAuth();
  const location = useLocation();
  const [dropOpen, setDropOpen] = useState(false);

  const crumbs = getBreadcrumb(location.pathname);

  return (
    <header style={{
      height: 'var(--topbar-h)', background: 'var(--surface)',
      borderBottom: '1px solid var(--border)',
      display: 'flex', alignItems: 'center', gap: 16,
      padding: '0 24px', position: 'sticky', top: 0, zIndex: 100,
    }}>
      {/* Mobile hamburger */}
      <button onClick={openMobile} className="topbar-hamburger" style={{
        display: 'none', background: 'none', border: 'none', cursor: 'pointer',
        color: 'var(--text2)', padding: 4, borderRadius: 6, alignItems: 'center',
      }}>
        <Menu size={20} />
      </button>

      {/* Breadcrumb */}
      <nav style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}>
        {crumbs.map((c, i) => (
          <React.Fragment key={i}>
            {i > 0 && <ChevronRight size={12} color="var(--text3)" />}
            <span style={{
              fontSize: 14, fontWeight: i === crumbs.length - 1 ? 600 : 300,
              color: i === crumbs.length - 1 ? 'var(--text)' : 'var(--text3)',
            }}>{c}</span>
          </React.Fragment>
        ))}
      </nav>

      {/* Right actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Search */}
        <button style={{
          display: 'flex', alignItems: 'center', gap: 8,
          background: 'var(--card)', border: '1px solid var(--border)',
          borderRadius: 8, padding: '7px 14px', cursor: 'pointer', color: 'var(--text3)',
          fontSize: 13, fontFamily: "'Inter',sans-serif", transition: 'all 0.15s',
        }} className="topbar-search"
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text3)'; }}
        >
          <Search size={13} /><span>Search…</span>
          <span style={{ marginLeft: 8, fontSize: 11, padding: '1px 5px', borderRadius: 4 }}>⌘K</span>
        </button>

        {/* Notification bell */}
        <button style={{
          position: 'relative', background: 'none', border: '1px solid var(--border)',
          borderRadius: 8, cursor: 'pointer', color: 'var(--text3)',
          padding: '7px', display: 'flex', alignItems: 'center', transition: 'all 0.15s',
        }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--border2)'; e.currentTarget.style.color = 'var(--text)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.color = 'var(--text3)'; }}
        >
          <Bell size={15} />
          <span style={{
            position: 'absolute', top: 5, right: 5, width: 6, height: 6,
            background: 'var(--or)', borderRadius: '50%', border: '1.5px solid var(--surface)',
          }} />
        </button>

        <ThemeToggle size={34} />

        {/* User avatar + dropdown */}
        <div style={{ position: 'relative' }}>
          <button onClick={() => setDropOpen(v => !v)} style={{
            display: 'flex', alignItems: 'center', gap: 10,
            background: 'var(--card)', border: '1px solid var(--border)',
            borderRadius: 10, padding: '6px 12px 6px 6px', cursor: 'pointer',
            transition: 'all 0.15s',
          }}
            onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--border2)'}
            onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
          >
            <div style={{
              width: 28, height: 28, borderRadius: '50%',
              background: 'linear-gradient(135deg, var(--or), var(--or2))',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 12, fontWeight: 600, color: '#fff', flexShrink: 0,
            }}>
              {user?.name?.split(' ').map(n => n[0]).join('') || 'A'}
            </div>
            <div style={{ textAlign: 'left' }} className="topbar-user-info">
              <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)', lineHeight: 1.2 }}>{user?.name || 'Admin'}</div>
              <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.2 }}>{user?.role}</div>
            </div>
          </button>

          {/* Dropdown */}
          {dropOpen && (
            <>
              <div onClick={() => setDropOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
              <div style={{
                position: 'absolute', top: 'calc(100% + 8px)', right: 0,
                width: 200, background: 'var(--card)', border: '1px solid var(--border2)',
                borderRadius: 12, overflow: 'hidden', zIndex: 20,
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)',
                animation: 'slideUp 0.2s ease both',
              }}>
                <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text)' }}>{user?.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 2 }}>{user?.email}</div>
                </div>
                {[
                  { icon: <User size={13} />, label: 'Profile', action: () => setDropOpen(false) },
                  { icon: <Settings size={13} />, label: 'Settings', action: () => setDropOpen(false) },
                ].map(item => (
                  <button key={item.label} onClick={item.action} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--text2)', fontSize: 14, fontFamily: "'Inter',sans-serif",
                    transition: 'background 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <span style={{ color: 'var(--text3)' }}>{item.icon}</span>{item.label}
                  </button>
                ))}
                <div style={{ borderTop: '1px solid var(--border)' }}>
                  <button onClick={() => { logout(); setDropOpen(false); }} style={{
                    display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                    padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
                    color: 'var(--red)', fontSize: 14, fontFamily: "'Inter',sans-serif",
                    transition: 'background 0.15s',
                  }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--red-d)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'none'}
                  >
                    <LogOut size={13} />Sign out
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      <style>{`
        @media (max-width: 1023px) { .topbar-hamburger { display: flex !important; } }
        @media (max-width: 600px)  { .topbar-search { display: none !important; } .topbar-user-info { display: none !important; } }
      `}</style>
    </header>
  );
}
